import { type FC, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useInterval } from 'usehooks-ts';

import { selectCurrentInProgressLoteryData } from '@/redux/selectors/lotterySelector';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const StartLotteryDrawAnimation: FC = () => {
  const currentLottery = useAppSelector(selectCurrentInProgressLoteryData);
  const [isAnimationActive, setIsAnimationActive] = useState(false);
  const [animateValue, setAnimateValue] = useState(3);
  const startAnimationTimerId = useRef<NodeJS.Timeout>();

  useInterval(
    () => {
      setAnimateValue((state) => (state > 0 ? state - 1 : 0));
    },
    !!animateValue && isAnimationActive ? 1000 : null,
  );

  useEffect(() => {
    if (!currentLottery?.finishedAt) {
      setIsAnimationActive(false);

      return;
    }

    const timeToStartDraw =
      new Date(currentLottery.finishedAt).getTime() - Date.now();

    if (timeToStartDraw <= 0) {
      setIsAnimationActive(false);

      return;
    }

    const secondsToStartDraw = Math.ceil(timeToStartDraw / 1000);

    const msToStartAnimation = (secondsToStartDraw - 3) * 1000;

    if (secondsToStartDraw > 3) {
      startAnimationTimerId.current = setTimeout(
        () => {
          console.log('start count down animation!');
          setIsAnimationActive(true);
        },
        msToStartAnimation > 86400000 ? 86400000 : msToStartAnimation,
      );
    } else {
      setIsAnimationActive(true);
      setAnimateValue(secondsToStartDraw);
    }

    return (): void => {
      clearTimeout(startAnimationTimerId.current);
      setIsAnimationActive(false);
    };
  }, [currentLottery?.finishedAt]);

  if (!currentLottery) {
    return null;
  }

  return (
    <AnimatePresence propagate>
      {!!currentLottery && isAnimationActive && (
        <motion.div
          className={css.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.span
            initial={{ scale: 2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.4,
              type: 'spring',
              mass: 1,
              stiffness: 675,
              damping: 30,
            }}
            key={animateValue || 'GO!'}
            data-value={animateValue || 'GO!'}
            className={cn(css.animateValue)}
          >
            <span>{animateValue || 'GO!'}</span>
          </motion.span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { StartLotteryDrawAnimation };
